import { Badge, Button, Card, Checkbox } from "../../../shared";
import {
  getEffectiveSystemDefault,
  isSystemDefaultOverridden,
} from "./systemDefaults";

/**
 * SuperAdmin editor for feature-flag system defaults.
 * These apply to every tenant unless a tier default or parent-company override wins.
 *
 * @param {object} props
 * @param {Array<object>} props.rows Grouped registry rows (module parents and standalone flags).
 * @param {Record<string, boolean>} props.draftDefaults Sparse overrides of the code registry.
 * @param {boolean} props.loaded Whether the system-defaults payload has loaded.
 * @param {string} [props.loadError] Message when the defaults request failed.
 * @param {boolean} props.saving Whether a save is in flight.
 * @param {(featureKey: string, meta: object, enabled: boolean) => void} props.onToggle
 * @param {(children: Array<[string, object]>) => void} props.onEnableChildren
 * @param {() => void} props.onSave
 * @param {() => void} props.onReset
 */
export function SystemDefaultsPanel({
  rows,
  draftDefaults,
  loaded,
  loadError,
  saving,
  onToggle,
  onEnableChildren,
  onSave,
  onReset,
}) {
  const customizedCount = Object.keys(draftDefaults || {}).length;

  /**
   * @param {string} featureKey
   * @param {object} meta
   * @param {{ compact?: boolean }} [opts]
   */
  function renderToggleRow(featureKey, meta, opts) {
    const checked = getEffectiveSystemDefault(featureKey, meta, draftDefaults);
    const overridden = isSystemDefaultOverridden(featureKey, draftDefaults);
    const compact = Boolean(opts?.compact);
    const codeDefault = Boolean(meta?.defaultEnabled);

    return (
      <div
        key={`system-default-${featureKey}`}
        className={`border border-gray-200 rounded-lg ${
          compact ? "p-2" : "p-3"
        } flex flex-col md:flex-row md:items-center md:justify-between gap-3`}
      >
        <div className="min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <div className="font-mono text-sm text-gray-900 truncate">
              {featureKey}
            </div>
            <Badge label={meta?.lifecycle || "ga"} size="sm" />
            <Badge
              color={checked ? "green" : "red"}
              label={checked ? "enabled" : "disabled"}
              size="sm"
            />
            <Badge
              color={overridden ? "blue" : "gray"}
              label={overridden ? "system" : "code"}
              size="sm"
            />
          </div>
          {meta?.description && !compact && (
            <div className="text-sm text-gray-600 mt-1">{meta.description}</div>
          )}
          <div className="text-xs text-gray-500 mt-1">
            Code default:{" "}
            <span className="font-medium">{codeDefault ? "on" : "off"}</span>
          </div>
        </div>
        <Checkbox
          id={`system-default-${featureKey}`}
          checked={checked}
          label="Enabled"
          onChange={(target) => {
            onToggle(featureKey, meta, Boolean(target?.checked));
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-base font-semibold text-gray-900">
              System Defaults
            </div>
            <div className="text-sm text-gray-600 mt-1">
              Baseline for every tenant. A saved tier value or a parent-company
              override still wins. Beta flags stay off in production until that
              tenant is on the latest channel and allowlisted, or has a Force
              On override.
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              type="light"
              onClick={onReset}
              disabled={!loaded || saving || customizedCount === 0}
            >
              Reset to code defaults
            </Button>
            <Button onClick={onSave} disabled={!loaded || saving} loading={saving}>
              Save
            </Button>
          </div>
        </div>

        <div className="mt-3 text-sm text-gray-500">
          {customizedCount === 0
            ? "This draft uses code defaults. Save to apply it to every tenant."
            : `${customizedCount} system override${
                customizedCount === 1 ? "" : "s"
              } in this draft. Save to apply them to every tenant.`}
        </div>

        {!loaded ? (
          <div className="mt-4 text-sm text-gray-600">
            {loadError || "Loading system defaults…"}
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            {(rows || []).map((row) => {
              if (row.kind === "group") {
                const parentChecked = getEffectiveSystemDefault(
                  row.parentKey,
                  row.parentMeta,
                  draftDefaults
                );
                const children = Array.isArray(row.children) ? row.children : [];
                const enabledChildrenCount = children.filter(([featureKey, meta]) =>
                  getEffectiveSystemDefault(featureKey, meta, draftDefaults)
                ).length;
                const allChildrenEnabled =
                  enabledChildrenCount === children.length && children.length > 0;

                return (
                  <div key={`system-group-${row.parentKey}`} className="space-y-2">
                    {renderToggleRow(row.parentKey, row.parentMeta)}
                    {parentChecked && children.length > 0 && (
                      <details className="ml-3 border-l border-gray-200 pl-3">
                        <summary className="cursor-pointer select-none flex items-center justify-between text-sm text-gray-700 hover:text-gray-900">
                          <span className="inline-flex items-center gap-2">
                            <span>Sub features</span>
                            <Badge
                              color={allChildrenEnabled ? "green" : "red"}
                              label={`${enabledChildrenCount}/${children.length}`}
                              size="sm"
                            />
                          </span>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-50"
                            disabled={allChildrenEnabled}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              onEnableChildren(children);
                            }}
                          >
                            Enable all
                          </button>
                        </summary>
                        <div className="mt-2 space-y-2">
                          {children.map(([childKey, childMeta]) =>
                            renderToggleRow(childKey, childMeta, { compact: true })
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                );
              }

              return renderToggleRow(row.featureKey, row.meta);
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

export default SystemDefaultsPanel;
